<template>
  <div v-if="properties.length" class="plist">
    <div
      v-for="property in properties"
      :key="property.id"
      class="pcard"
      :class="{ 'pcard--off': property.disabled }"
      @click="$refs.detail.open(property)"
    >
      <div class="pcard__thumb">
        <img
          v-if="property.images.length"
          :src="url + property.images[0].url"
          alt=""
        />
        <img v-else :src="require('@/assets/placeholders/no-image.jpg')" alt="" />
      </div>

      <div class="pcard__main">
        <div class="pcard__type">
          <span>{{ $t("property.type." + property.propertyType.toLowerCase()) }}</span>
          <v-icon
            size="15"
            :color="isVisible(property) ? '#6c8762' : '#c0392b'"
            :title="isVisible(property) ? 'Visible en la web' : 'No visible en la web'"
          >
            {{ isVisible(property) ? "mdi-eye" : "mdi-eye-off" }}
          </v-icon>
        </div>

        <div class="pcard__addr">
          {{ property.address.street }} {{ property.address.number }}
          {{ property.address.unit }}, {{ property.address.city }}
        </div>

        <div v-if="specs(property).length" class="pcard__specs">
          <span v-for="(s, i) in specs(property)" :key="i" class="pcard__spec">
            <v-icon size="15">{{ s.icon }}</v-icon>{{ s.value }}
          </span>
        </div>
      </div>

      <div class="pcard__meta">
        <span class="pbadge" :class="'pbadge--' + property.state.toLowerCase()">
          {{ $t("property.state." + property.state.toLowerCase()) }}
        </span>

        <div class="pcard__price">
          <div v-for="(line, i) in priceLines(property)" :key="i" class="pcard__amount">
            {{ line }}
          </div>
        </div>

        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="pcard__kebab"
              v-on="on"
              v-bind="attrs"
              @click.stop
            >
              <v-icon size="20">mdi-dots-vertical</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-list-item
              @click="$router.push({ name: 'EditProperty', params: { id: property.id } })"
            >
              <v-list-item-icon class="mr-3"><v-icon size="18">mdi-pencil</v-icon></v-list-item-icon>
              <v-list-item-title>Editar</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleting = property.id">
              <v-list-item-icon class="mr-3"><v-icon size="18" color="error">mdi-delete</v-icon></v-list-item-icon>
              <v-list-item-title>Eliminar</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <property-detail ref="detail" @reload="$emit('reload')"></property-detail>

    <Modal ref="deleting">
      <v-card>
        <v-card-title>
          {{ $t("property.list.delete_modal.title") }}
        </v-card-title>
        <v-card-text>
          {{ $t("property.list.delete_modal.confirm_message") }}
        </v-card-text>
        <v-card-actions class="px-6 py-3 justify-end">
          <v-spacer></v-spacer>
          <v-btn text @click.stop="deleting = null">
            {{ $t("action.cancel") }}
          </v-btn>
          <v-btn color="error" depressed class="rounded-lg" @click.stop="deleteProperty">
            {{ $t("action.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </Modal>
  </div>

  <div v-else class="pcard-empty">
    {{ $t("property.list.no_properties_found") }}
    <router-link :to="{ name: 'CreateProperty' }">
      {{ $t("action.click_here") }}</router-link
    >.
  </div>
</template>

<script>
import { DeleteProperty } from "@/graphql/mutations/properties";
import { mapState } from "vuex";
import Modal from "@/components/modals/NoActivator";
import PropertyDetail from "@/components/properties/Detail";

export default {
  data() {
    return {
      deleting: null
    };
  },

  props: ["properties"],

  components: {
    Modal,
    PropertyDetail
  },

  watch: {
    deleting(newVal) {
      newVal == null
        ? (this.$refs.deleting.dialog = false)
        : (this.$refs.deleting.dialog = true);
    }
  },

  computed: {
    ...mapState(["url"])
  },

  methods: {
    isVisible(property) {
      return (
        !property.disabled && ["published", "booked"].includes(property.state)
      );
    },

    specs(property) {
      const chars = property.characteristics || [];
      const find = name => {
        const c = chars.find(c => c.name === name);
        const v = c && c.value;
        return v && v !== "0" && String(v).trim() !== "" ? v : null;
      };
      const out = [];
      const beds = find("Dormitorios");
      if (beds) out.push({ icon: "mdi-bed-king-outline", value: beds });
      const baths = find("Baños");
      if (baths) out.push({ icon: "mdi-shower", value: baths });
      const surf = find("Superficie total");
      if (surf) out.push({ icon: "mdi-arrow-expand", value: surf + " m²" });
      return out;
    },

    priceLines(property) {
      const out = [];
      (property.operationTypes || []).forEach(op => {
        if (op === "rent" && property.price && property.price !== "$0,00")
          out.push(property.price);
        if (op === "sell" && property.sellPrice && property.sellPrice !== "$0,00")
          out.push(property.sellPrice);
      });
      return out;
    },

    deleteProperty() {
      this.$apollo
        .mutate({
          mutation: DeleteProperty,
          variables: {
            input: {
              id: this.deleting
            }
          }
        })
        .then(res => {
          if (res.data.deleteProperty.success) {
            this.$refs.deleting.dialog = false;
            this.$emit("reload");
          }
        });
    }
  }
};
</script>

<style scoped>
.pcard {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.pcard:hover {
  border-color: #dcd4c6;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}
.pcard--off { background: #faf8f4; opacity: 0.85; }

.pcard__thumb {
  flex: none;
  width: 96px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  background: #ece7de;
}
.pcard__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

.pcard__main { flex: 1; min-width: 160px; }
.pcard__type {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  color: #a39a8c;
}
.pcard__addr {
  font-size: 14.5px;
  font-weight: 600;
  color: #1a1814;
  margin: 3px 0 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pcard__specs { display: flex; flex-wrap: wrap; gap: 4px 14px; color: #756f64; font-size: 12.5px; }
.pcard__spec { display: inline-flex; align-items: center; gap: 4px; }
.pcard__spec .v-icon { color: #a39a8c; }

.pcard__meta { flex: none; display: flex; align-items: center; gap: 16px; }
.pbadge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
  line-height: 1.6;
}
.pbadge--published { background: #e3f3e8; color: #2c6e49; }
.pbadge--booked { background: #fdeecf; color: #8a5a00; }
.pbadge--rented { background: #e1ecff; color: #2353b8; }
.pbadge--draft { background: #ece9e3; color: #6a645a; }

.pcard__price { min-width: 96px; text-align: right; }
.pcard__amount { font-size: 14px; font-weight: 600; color: #1a1814; white-space: nowrap; }

.pcard__kebab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #756f64;
  transition: background 0.15s ease, color 0.15s ease;
}
.pcard__kebab:hover { background: #f1ece3; color: #1a1814; }

.pcard-empty {
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #a39a8c;
  font-size: 14px;
}
.pcard-empty a { color: #b52118; font-weight: 600; }

@media (max-width: 720px) {
  /* image + info stay side-by-side; meta drops to a full-width bottom row */
  .pcard { align-items: flex-start; }
  .pcard__main { min-width: 0; }
  .pcard__addr {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .pcard__meta {
    width: 100%;
    justify-content: space-between;
    border-top: 1px solid #f1ece3;
    padding-top: 10px;
  }
  .pcard__price { text-align: left; }
}
</style>
